import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Info, Loader2 } from "lucide-react";
import { api } from "../api";
import { pickInitialYear } from "../lib/kiedys.mjs";

// KIEDYŚ/DZIŚ — parcel temporal intelligence (P1). Doctrine: availability only
// from the Skorowidze parcel intersection (backend-gated); blank tiles are
// honest states; pre-1995 = index/download only; no AI change conclusions.
const PL = {
  title: "Historia działki — KIEDYŚ / DZIŚ",
  subtitle: "Autorytatywne zobrazowania GUGiK/Geoportal. Wyświetlamy wyłącznie lata potwierdzone dla tej działki — luki w osi czasu są częścią dowodu.",
  swipe: "Przesuń", side: "Obok siebie",
  exact: "Dokładnie działka", ctx: "Kontekst ~120 m",
  res25: "25 cm", res10: "do 10 cm (2012+)",
  today: "DZIŚ", then: "KIEDYŚ",
  indexOnly: "tylko indeks — bez renderu interaktywnego",
  loading: "Wczytywanie zobrazowania…",
  nocov: "Brak zobrazowania dla tej działki w tym okresie. Pusty obraz nigdy nie jest pokazywany jako zobrazowanie.",
  unavail: "Źródło zobrazowań chwilowo niedostępne — to nie jest informacja o braku zobrazowania.",
  noArchive: "Brak zobrazowań archiwalnych w indeksie Skorowidz dla tej działki.",
  prov: "Proweniencja",
  provider: "Dostawca", dataset: "Zbiór / warstwa", year: "Rok zobrazowania",
  gsd: "Rozdzielczość (GSD)", retrieved: "Pobrano", source: "Źródło",
  exactDate: "Dokładna data pozyskania", unknown: "nieznana",
  sheet: "Arkusz", semantics: "Semantyka czasu WMS", mosaic: "mozaika aktualna na żądaną datę", current: "zobrazowanie bieżące",
  download: "pobierz arkusz (opendata)",
  noticeNotConfirmed: (y) => `Rok ${y} nie jest potwierdzony dla tej działki w indeksie Skorowidz — nigdy nie podmieniamy lat. Wybrano najnowszy potwierdzony rok.`,
  noticeDownloadOnly: (y) => `Rok ${y}: zobrazowanie udokumentowane w indeksie Skorowidz, dostępne wyłącznie do pobrania arkusza (bez renderu interaktywnego). Wybrano najnowszy rok z renderem.`,
};

const chip = "rounded-md border px-2 py-1 text-xs font-semibold transition-colors";

export function KiedysDzis({ parcelId, lang = "pl" }) {
  const [av, setAv] = useState(null);
  const [avErr, setAvErr] = useState(false);
  const [year, setYear] = useState(null);
  const [service, setService] = useState("standard");
  const [mode, setMode] = useState("swipe");
  const [context, setContext] = useState(120);
  const [divider, setDivider] = useState(50);
  const [showProv, setShowProv] = useState(false);
  const [deepNotice, setDeepNotice] = useState(null);
  const [imgs, setImgs] = useState({});
  const frame = useRef(null);

  useEffect(() => {
    setMode(window.innerWidth < 640 ? "swipe" : "side");
    api.get(`/imagery/${parcelId}/availability`)
      .then((r) => {
        const im = r.data.imagery;
        setAv(im);
        // Shareable Historia działki deep link: ?kiedys=YYYY (validated against
        // this parcel's Skorowidze-confirmed periods; honest notice otherwise).
        const raw = new URLSearchParams(window.location.search).get("kiedys");
        const initial = pickInitialYear(im.periods || [], raw);
        if (initial.year) setYear(initial.year);
        if (initial.notice) setDeepNotice(initial.notice);
      })
      .catch(() => setAvErr(true));
  }, [parcelId]);

  const ctxQ = `&context_m=${context}&overlay=1`;
  const dzisUrl = `/imagery/${parcelId}/render?kind=dzis${ctxQ}`;
  const kiedysUrl = year
    ? `/imagery/${parcelId}/render?kind=kiedys&service=${service}&year=${year}${ctxQ}`
    : null;

  const load = (key, url) => {
    if (!url) return;
    setImgs((s) => ({ ...s, [key]: { state: "loading" } }));
    api.get(url, { responseType: "blob" })
      .then((r) => {
        let prov = {};
        try { prov = JSON.parse(r.headers["x-imagery-provenance"] || "{}"); } catch { /* header optional */ }
        setImgs((s) => ({ ...s, [key]: { state: "ok", prov } }));
      })
      .catch((e) => {
        const st = e.response?.status;
        setImgs((s) => ({ ...s, [key]: { state: st === 404 ? "nocov" : st === 503 ? "unavail" : "err" } }));
      });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load("dzis", dzisUrl); }, [dzisUrl]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load("kiedys", kiedysUrl); }, [kiedysUrl]);

  const onDrag = (e) => {
    if (!frame.current || (e.type === "pointermove" && !e.buttons)) return;
    const r = frame.current.getBoundingClientRect();
    setDivider(Math.min(96, Math.max(4, ((e.clientX - r.left) / r.width) * 100)));
  };

  const period = (av?.periods || []).find((p) => p.year === year);
  const provPanel = (label, img, p) => (
    <div className="min-w-0 flex-1 rounded-lg border border-slate-200 p-3" data-testid={`kdz-prov-${label.toLowerCase()}`}>
      <p className="text-xs font-bold text-slate-700">{label}</p>
      <dl className="mt-1 space-y-0.5 text-xs text-slate-600">
        <div><dt className="inline font-semibold">{PL.provider}: </dt><dd className="inline">{img.prov?.authority || "GUGiK / Geoportal"}</dd></div>
        <div><dt className="inline font-semibold">{PL.dataset}: </dt><dd className="inline">{img.prov?.dataset || PL.unknown}</dd></div>
        {p ? (<>
          <div><dt className="inline font-semibold">{PL.year}: </dt><dd className="inline">{p.year}</dd></div>
          <div><dt className="inline font-semibold">{PL.exactDate}: </dt><dd className="inline">{p.exact_date || PL.unknown}</dd></div>
          <div><dt className="inline font-semibold">{PL.gsd}: </dt><dd className="inline">{p.gsd_m ? `${p.gsd_m} m` : PL.unknown}</dd></div>
          <div><dt className="inline font-semibold">{PL.sheet}: </dt><dd className="inline">{p.sheet || PL.unknown}</dd></div>
          <div><dt className="inline font-semibold">{PL.semantics}: </dt><dd className="inline">{PL.mosaic}</dd></div>
        </>) : (
          <div><dt className="inline font-semibold">{PL.year}: </dt><dd className="inline">{PL.current}</dd></div>
        )}
        <div><dt className="inline font-semibold">{PL.retrieved}: </dt><dd className="inline">{(img.prov?.retrieved_at || "").slice(0, 19).replace("T", " ") || PL.unknown}</dd></div>
        <div><dt className="inline font-semibold">{PL.source}: </dt><dd className="inline break-all">
          {img.prov?.url ? <a href={img.prov.url} target="_blank" rel="noopener noreferrer" className="text-[#DC143C] hover:underline">Geoportal WMS</a> : PL.unknown}
          {p?.download_url && <> · <a href={p.download_url} target="_blank" rel="noopener noreferrer" className="text-[#DC143C] hover:underline">{PL.download}</a></>}
        </dd></div>
      </dl>
    </div>
  );

  const imgBox = (key, testId, label, url) => {
    const im = imgs[key] || {};
    return (
      <div className="relative aspect-square w-full overflow-hidden rounded-xl border bg-slate-100" data-testid={testId}>
        {im.state === "ok" && <img src={`/api${url}`} alt={label} className="absolute inset-0 h-full w-full object-cover" />}
        {im.state === "loading" && (
          <div className="absolute inset-0 flex items-center justify-center text-xs text-slate-500">
            <Loader2 size={16} className="mr-2 animate-spin" />{PL.loading}
          </div>)}
        {(im.state === "nocov" || im.state === "err") && (
          <div className="absolute inset-0 flex items-center justify-center p-4 text-center text-xs text-slate-600" data-testid={`${testId}-nocov`}>{PL.nocov}</div>)}
        {im.state === "unavail" && (
          <div className="absolute inset-0 flex items-center justify-center p-4 text-center text-xs text-slate-600" data-testid={`${testId}-unavail`}>{PL.unavail}</div>)}
        <span className="absolute left-2 top-2 rounded bg-slate-900/80 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">{label}</span>
      </div>
    );
  };

  return (
    <section data-testid="kdz-section" className="mt-8">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="font-heading text-lg font-semibold">{PL.title}</h2>
        <button data-testid="kdz-provenance-toggle" onClick={() => setShowProv(!showProv)}
          className="rounded-md border border-slate-300 p-1.5 text-slate-500 hover:border-slate-400 hover:text-slate-700" aria-label={PL.prov}>
          <Info size={14} />
        </button>
      </div>
      <p className="mt-1 text-xs text-slate-500">{PL.subtitle}</p>

      {deepNotice && (
        <p className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-xs text-slate-700" data-testid="kdz-deeplink-notice">
          {deepNotice.code === "DOWNLOAD_ONLY" ? PL.noticeDownloadOnly(deepNotice.year) : PL.noticeNotConfirmed(deepNotice.year)}
        </p>
      )}

      {avErr && <p className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-slate-700" data-testid="kdz-unavailable">{PL.unavail}</p>}

      {av && (
        <>
          <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="kdz-timeline">
            {(av.periods || []).map((p) => p.render === "WMS_TIME" ? (
              <button key={p.year} data-testid={`kdz-year-${p.year}`} onClick={() => { setYear(p.year); setDeepNotice(null); if (p.year < 2012) setService("standard"); }}
                className={`${chip} ${year === p.year ? "border-[#DC143C] bg-[#DC143C] text-white" : "border-slate-300 text-slate-700 hover:border-[#DC143C]"}`}>
                {p.year}
              </button>
            ) : (
              <span key={p.year} data-testid={`kdz-year-${p.year}`} title={PL.indexOnly}
                className={`${chip} cursor-help border-dashed border-slate-300 text-slate-400`}>
                {p.year}
                {p.download_url && <a href={p.download_url} target="_blank" rel="noopener noreferrer" className="ml-1 text-[#DC143C]" data-testid={`kdz-download-${p.year}`}>↧</a>}
              </span>
            ))}
            <span className={`${chip} border-emerald-600 bg-emerald-600 text-white`} data-testid="kdz-dzis-chip">{PL.today}</span>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
            <div className="flex overflow-hidden rounded-md border border-slate-300" data-testid="kdz-mode-toggle">
              <button data-testid="kdz-mode-swipe" onClick={() => setMode("swipe")}
                className={`px-2.5 py-1.5 font-semibold ${mode === "swipe" ? "bg-slate-900 text-white" : "text-slate-600"}`}>{PL.swipe}</button>
              <button data-testid="kdz-mode-side" onClick={() => setMode("side")}
                className={`px-2.5 py-1.5 font-semibold ${mode === "side" ? "bg-slate-900 text-white" : "text-slate-600"}`}>{PL.side}</button>
            </div>
            <div className="flex overflow-hidden rounded-md border border-slate-300" data-testid="kdz-context-toggle">
              <button data-testid="kdz-context-exact" onClick={() => setContext(0)}
                className={`px-2.5 py-1.5 font-semibold ${context === 0 ? "bg-slate-900 text-white" : "text-slate-600"}`}>{PL.exact}</button>
              <button data-testid="kdz-context-120" onClick={() => setContext(120)}
                className={`px-2.5 py-1.5 font-semibold ${context === 120 ? "bg-slate-900 text-white" : "text-slate-600"}`}>{PL.ctx}</button>
            </div>
            {year >= 2012 && (
              <div className="flex overflow-hidden rounded-md border border-slate-300" data-testid="kdz-res-toggle">
                <button data-testid="kdz-res-standard" onClick={() => setService("standard")}
                  className={`px-2.5 py-1.5 font-semibold ${service === "standard" ? "bg-slate-900 text-white" : "text-slate-600"}`}>{PL.res25}</button>
                <button data-testid="kdz-res-high" onClick={() => setService("high")}
                  className={`px-2.5 py-1.5 font-semibold ${service === "high" ? "bg-slate-900 text-white" : "text-slate-600"}`}>{PL.res10}</button>
              </div>
            )}
          </div>

          {av.status === "NO_COVERAGE" && (
            <p className="mt-3 rounded-lg border border-slate-300 bg-slate-50 p-3 text-xs text-slate-600" data-testid="kdz-no-archive">{PL.noArchive}</p>
          )}

          {mode === "swipe" ? (
            <div ref={frame} data-testid="kdz-swipe" onPointerDown={onDrag} onPointerMove={onDrag}
              className="relative mt-3 aspect-square w-full cursor-ew-resize touch-none select-none overflow-hidden rounded-xl border bg-slate-100">
              <div className="absolute inset-0">{imgBox("kiedys", "kdz-kiedys-img", `${PL.then} ${year || ""}`, kiedysUrl)}</div>
              <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - divider}% 0 0)` }} data-testid="kdz-dzis-clip">
                {imgBox("dzis", "kdz-dzis-img", PL.today, dzisUrl)}
              </div>
              <div className="absolute inset-y-0 z-10 w-0.5 bg-white shadow-[0_0_4px_rgba(0,0,0,0.6)]" style={{ left: `${divider}%` }} data-testid="kdz-swipe-divider">
                <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-slate-900/70 px-2 py-1 text-[10px] font-bold text-white">⇔</span>
              </div>
            </div>
          ) : (
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="kdz-side">
              {imgBox("kiedys", "kdz-kiedys-img", `${PL.then} ${year || ""}`, kiedysUrl)}
              {imgBox("dzis", "kdz-dzis-img", PL.today, dzisUrl)}
            </div>
          )}

          {showProv && (
            <div className="mt-3 flex flex-col gap-2 sm:flex-row" data-testid="kdz-provenance-panel">
              {year && provPanel(`${PL.then} ${year}`, imgs.kiedys || {}, period)}
              {provPanel(PL.today, imgs.dzis || {}, null)}
            </div>
          )}
          <p className="mt-2 text-[10px] text-slate-400">Źródło: GUGiK / Geoportal · obraz granicy działki z EGiB · {av.provenance?.method || ""}</p>
          <Link to="/edu" data-testid="kdz-edu-link"
            className="mt-2 inline-block text-xs font-medium text-[#DC143C] underline-offset-2 hover:underline">
            Dla uczelni i badań →
          </Link>
        </>
      )}
    </section>
  );
}
